import type { Chart, LineConfig } from "@ant-design/charts";

export const MOBILE_TOOLTIP_HIDE_TIMEOUT_MS = 5_000;
export const UNIFIED_CHART_WRAPPER_CLASS = "zlp-unified-chart-wrap";

const CHART_CLEANUP_KEY = "__zlpUnifiedTooltipCleanup";

type ChartWithInternals = Chart & {
  [CHART_CLEANUP_KEY]?: () => void;
  container?: HTMLElement;
  chart?: {
    emit?: (eventName: string, payload?: unknown) => void;
    hideTooltip?: () => void;
  };
  getContainer?: () => HTMLElement;
  emit?: (eventName: string, payload?: unknown) => void;
  on?: (eventName: string, handler: (...args: unknown[]) => void) => void;
  off?: (eventName: string, handler: (...args: unknown[]) => void) => void;
  hideTooltip?: () => void;
};

function getRuntimeChart(chart: ChartWithInternals): {
  emit?: (eventName: string, payload?: unknown) => void;
  hideTooltip?: () => void;
} {
  const runtime = chart.chart;
  if (runtime && typeof runtime == "object") {
    return runtime;
  }
  return chart;
}

function hideTooltip(chart: ChartWithInternals) {
  const runtimeChart = getRuntimeChart(chart);

  if (typeof runtimeChart.hideTooltip == "function") {
    try {
      runtimeChart.hideTooltip();
      return;
    } catch {
      // Ignore internal chart errors during rapid re-render/slider interactions.
    }
  }

  if (typeof runtimeChart.emit == "function") {
    try {
      runtimeChart.emit("tooltip:hide", { nativeEvent: false });
    } catch {
      // Ignore internal chart errors during rapid re-render/slider interactions.
    }
  }
}

function getChartContainer(chart: ChartWithInternals): HTMLElement | undefined {
  const fromGetter = chart.getContainer?.();
  if (fromGetter instanceof HTMLElement) return fromGetter;
  if (chart.container instanceof HTMLElement) return chart.container;
  const fromCanvasContext = (
    chart as ChartWithInternals & {
      context?: { canvas?: { getContainer?: () => HTMLElement } };
    }
  ).context?.canvas?.getContainer?.();
  if (fromCanvasContext instanceof HTMLElement) return fromCanvasContext;
  return undefined;
}

function attachDesktopLifecycle(chart: ChartWithInternals): () => void {
  const container = getChartContainer(chart);
  if (!container) return () => {};

  const onPointerLeave = () => {
    hideTooltip(chart);
  };

  container.addEventListener("pointerleave", onPointerLeave, true);

  return () => {
    container.removeEventListener("pointerleave", onPointerLeave, true);
  };
}

function attachMobileLifecycle(chart: ChartWithInternals): () => void {
  const container = getChartContainer(chart);
  if (!container) return () => {};

  let hideTimer: number | undefined;

  const clearHideTimer = () => {
    if (hideTimer == undefined) return;
    window.clearTimeout(hideTimer);
    hideTimer = undefined;
  };

  const scheduleHide = () => {
    clearHideTimer();
    hideTimer = window.setTimeout(() => {
      hideTooltip(chart);
      hideTimer = undefined;
    }, MOBILE_TOOLTIP_HIDE_TIMEOUT_MS);
  };

  const onTapInside = () => {
    scheduleHide();
  };

  const onTooltipShow = () => {
    scheduleHide();
  };

  const onPointerDownOutside = (event: Event) => {
    const target = event.target as Node | null;
    if (!target) return;

    if (container.contains(target)) return;

    clearHideTimer();
    hideTooltip(chart);
  };

  container.addEventListener("pointerdown", onTapInside, true);
  container.addEventListener("touchstart", onTapInside, {
    capture: true,
    passive: true,
  });
  chart.on?.("tooltip:show", onTooltipShow);
  document.addEventListener("pointerdown", onPointerDownOutside, true);
  document.addEventListener("touchstart", onPointerDownOutside, true);

  return () => {
    clearHideTimer();

    container.removeEventListener("pointerdown", onTapInside, true);
    container.removeEventListener("touchstart", onTapInside, true);
    chart.off?.("tooltip:show", onTooltipShow);
    document.removeEventListener("pointerdown", onPointerDownOutside, true);
    document.removeEventListener("touchstart", onPointerDownOutside, true);
  };
}

function bindUnifiedTooltipLifecycle(chart: ChartWithInternals, isMobile: boolean) {
  chart[CHART_CLEANUP_KEY]?.();
  chart[CHART_CLEANUP_KEY] = isMobile
    ? attachMobileLifecycle(chart)
    : attachDesktopLifecycle(chart);

  const onChartDestroy = () => {
    chart[CHART_CLEANUP_KEY]?.();
    chart[CHART_CLEANUP_KEY] = undefined;
    chart.off?.("afterdestroy", onChartDestroy);
  };

  chart.on?.("afterdestroy", onChartDestroy);
}

export function applyUnifiedLineTooltipBehavior(
  config: LineConfig,
  isMobile: boolean
): LineConfig {
  return applyUnifiedLineChartConfig(config, { isMobile });
}

export function applyUnifiedLineChartConfig(
  config: LineConfig,
  options: {
    isMobile: boolean;
    forceAutoFit?: boolean;
  }
): LineConfig {
  const { isMobile, forceAutoFit } = options;
  const baseOnReady = config.onReady;
  const autoFit =
    forceAutoFit != undefined ? forceAutoFit : (config.autoFit ?? true);

  return {
    ...config,
    autoFit,
    onReady: (chart: Chart) => {
      baseOnReady?.(chart);
      if (typeof window == "undefined") return;
      bindUnifiedTooltipLifecycle(chart as ChartWithInternals, isMobile);
    },
  };
}
